import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, CheckCircle, FileText, Loader2, RefreshCcw, Trash2, Search, X } from 'lucide-react';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { useAppContext } from '../../context/AppContext';
import Modal from '../ui/Modal';
import StorageUsageBar from '../storage/StorageUsageBar';
import { deleteDocument, clearAllDocuments, fetchDocumentFile, fetchDocumentList } from '../../services/documents';

const ALLOWED_ROLES = ['admin', 'regular'];
const PREVIEWABLE_EXTENSIONS = new Set(['docx', 'pdf', 'csv', 'xlsx', 'pptx', 'tif', 'tiff', 'png', 'jpg', 'jpeg', 'bmp', 'gif']);
const MAX_TABLE_ROWS = 150;
const MAX_TABLE_COLUMNS = 30;

const getFileExtension = (name = '') => {
  const parts = name.toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() : '';
};

const getFolderIcon = () => '📁';

const groupDocumentsByFolder = (documents) => {
  const grouped = {};
  
  documents.forEach(doc => {
    const folder = doc.folder || '(Root)';
    if (!grouped[folder]) {
      grouped[folder] = [];
    }
    grouped[folder].push(doc);
  });
  
  return grouped;
};

const normalizeTableData = (rows) => {
  if (!rows || rows.length === 0) {
    return { headers: [], rows: [], truncatedRows: false, truncatedColumns: false };
  }

  const maxColsInData = rows.reduce((max, row) => Math.max(max, row.length), 0);
  const maxColumns = Math.min(MAX_TABLE_COLUMNS, maxColsInData || 0);
  if (maxColumns === 0) {
    return { headers: [], rows: [], truncatedRows: rows.length > MAX_TABLE_ROWS, truncatedColumns: false };
  }
  const limitedRows = rows.slice(0, MAX_TABLE_ROWS);

  const headers = Array.from({ length: maxColumns }, (_, idx) => {
    const headerValue = limitedRows[0]?.[idx];
    if (headerValue === undefined || headerValue === null || headerValue === '') {
      return `Column ${idx + 1}`;
    }
    return String(headerValue);
  });

  const body = limitedRows.slice(1).map((row) =>
    Array.from({ length: maxColumns }, (_, idx) => {
      const cell = row[idx];
      if (cell === undefined || cell === null) return '';
      return typeof cell === 'string' ? cell : String(cell);
    })
  );

  return {
    headers,
    rows: body,
    truncatedRows: rows.length > MAX_TABLE_ROWS,
    truncatedColumns: maxColsInData > MAX_TABLE_COLUMNS,
  };
};

const DocumentPreviewer = ({ isOpen, onClose, initialDocName }) => {
  const { token, theme, user, setDocumentPreviewTarget } = useAppContext();
  const [documents, setDocuments] = useState([]);
  const [listState, setListState] = useState('idle');
  const [previewState, setPreviewState] = useState('idle');
  const [selectedDocPath, setSelectedDocPath] = useState(null); // Changed from selectedDoc to selectedDocPath
  const [previewContent, setPreviewContent] = useState({ type: 'none', data: null });
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteState, setDeleteState] = useState('idle');
  const [deleteError, setDeleteError] = useState(null);
  const [clearModalOpen, setClearModalOpen] = useState(false);
  const [clearState, setClearState] = useState('idle');
  const [clearError, setClearError] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set(['(Root)'])); // Track expanded folders
  const [searchQuery, setSearchQuery] = useState(''); // Search filter
  const pdfUrlRef = useRef(null);

  const hasAccess = useMemo(
    () => ALLOWED_ROLES.includes((user?.user_type || '').toLowerCase()),
    [user]
  );

  const isAdmin = useMemo(
    () => (user?.user_type || '').toLowerCase() === 'admin',
    [user]
  );

  // Filter documents based on search query
  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) {
      return documents;
    }
    const lowerQuery = searchQuery.toLowerCase();
    return documents.filter(doc =>
      doc.name.toLowerCase().includes(lowerQuery) ||
      (doc.folder && doc.folder.toLowerCase().includes(lowerQuery))
    );
  }, [documents, searchQuery]);

  const resetPreviewContent = useCallback(() => {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }
    setPreviewContent({ type: 'none', data: null });
  }, []);

  const loadDocuments = useCallback(async () => {
    if (!token) {
      setError('Missing authentication token');
      setListState('error');
      return;
    }
    setListState('loading');
    setError(null);
    try {
      const list = await fetchDocumentList(token);
      const filesOnly = Array.isArray(list) ? list.filter((entry) => !entry.is_dir) : [];
      setDocuments(filesOnly);      
      setListState('ready');
    } catch (err) {
      setError(err.message || 'Failed to fetch documents');
      setListState('error');
    }
  }, [token]);

  const handlePreview = useCallback(async (doc) => {
    if (!token) {
      setError('Missing authentication token');
      setPreviewState('error');
      return;
    }
    setSelectedDocPath(doc.path);
    setPreviewState('loading');
    setError(null);
    resetPreviewContent();
    try {
      const extension = getFileExtension(doc.name);
      if (!PREVIEWABLE_EXTENSIONS.has(extension)) {
        setPreviewContent({ type: 'none', data: null });
        setPreviewState('ready');
        return;
      }

      // Use doc.path instead of doc.name for fetching
      const { buffer, contentType } = await fetchDocumentFile(doc.path, token);

      if (extension === 'docx') {
        const { value } = await mammoth.convertToHtml({ arrayBuffer: buffer });
        const baseHtml = value || '<p>No preview content available.</p>';
        const withPageBreaks = baseHtml
          .replace(
            /<p[^>]*class="[^"]*page-break[^"]*"[^>]*>\s*<\/p>/gi,
            '<div class="page-separator" aria-hidden="true"></div>'
          )
          .replace(
            /<(p|div)[^>]*style="[^"]*page-break-[^"]*"[^>]*>[\s\S]*?<\/\1>/gi,
            '<div class="page-separator" aria-hidden="true"></div>'
          )
          .replace(
            /<br[^>]*page-break[^>]*>/gi,
            '<div class="page-separator" aria-hidden="true"></div>'
          );
        setPreviewContent({ type: 'html', data: withPageBreaks });
      } else if (extension === 'pdf' || extension === 'pptx' || extension === 'tif' || extension === 'tiff' || extension === 'png' || extension === 'jpg' || extension === 'jpeg' || extension === 'bmp' || extension === 'gif') {
        // PPTX files are converted to PDF on the backend
        // Image files are also converted to PDF on the backend
        const blob = new Blob([buffer], { type: contentType || 'application/pdf' });
        const url = URL.createObjectURL(blob);
        pdfUrlRef.current = url;
        setPreviewContent({ type: 'pdf', data: url });
      } else if (extension === 'csv' || extension === 'xlsx') {
        const workbook = XLSX.read(new Uint8Array(buffer), { type: 'array' });
        if (!workbook.SheetNames.length) {
          throw new Error('No sheets found in file');
        }
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' });
        const tableData = normalizeTableData(rows);
        setPreviewContent({ type: 'table', data: tableData });
      } else {
        throw new Error('Unsupported file type for preview');
      }
      setPreviewState('ready');
    } catch (err) {
      setError(err.message || 'Failed to load document preview');
      setPreviewState('error');
    }
  }, [token, resetPreviewContent]);

  const openDeleteModal = useCallback((doc) => {
    setDeleteTarget(doc);
    setDeleteState('idle');
    setDeleteError(null);
  }, []);

  const closeDeleteModal = useCallback(() => {
    if (deleteState === 'loading') {
      return;
    }
    setDeleteTarget(null);
    setDeleteError(null);
  }, [deleteState]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) {
      return;
    }
    if (!token) {
      setDeleteError('Missing authentication token');
      setDeleteState('error');
      return;
    }

    setDeleteState('loading');
    setDeleteError(null);
    setNotice(null);

    try {
      const result = await deleteDocument(deleteTarget.path, token);
      if (selectedDocPath === deleteTarget.path) {
        setSelectedDocPath(null);
        setDocumentPreviewTarget(null);
        resetPreviewContent();
        setPreviewState('idle');
      }
      await loadDocuments();
      setNotice(result?.message || `Deleted ${deleteTarget.name}.`);
      setDeleteState('success');
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete document');
      setDeleteState('error');
    }
  }, [deleteTarget, token, selectedDocPath, resetPreviewContent, loadDocuments, setDocumentPreviewTarget]);

  const openClearModal = useCallback(() => {
    setClearModalOpen(true);
    setClearState('idle');
    setClearError(null);
  }, []);

  const closeClearModal = useCallback(() => {
    if (clearState === 'loading') {
      return;
    }
    setClearModalOpen(false);
    setClearError(null);
  }, [clearState]);

  const confirmClearAll = useCallback(async () => {
    if (!token) {
      setClearError('Missing authentication token');
      setClearState('error');
      return;
    }

    setClearState('loading');
    setClearError(null);
    setNotice(null);

    try {
      const result = await clearAllDocuments(token);
      setSelectedDocPath(null);
      setDocumentPreviewTarget(null);
      resetPreviewContent();
      setPreviewState('idle');
      await loadDocuments();
      setNotice(result?.message || 'Knowledge base cleared successfully.');
      setClearState('success');
      setClearModalOpen(false);
    } catch (err) {
      setClearError(err.message || 'Failed to clear knowledge base');
      setClearState('error');
    }
  }, [token, resetPreviewContent, loadDocuments, setDocumentPreviewTarget]);

  useEffect(() => {
    if (!isOpen) {
      setDocumentPreviewTarget(null);
      setSelectedDocPath(null);
      resetPreviewContent();
      setPreviewState('idle');
      setError(null);
      setNotice(null);
      setClearModalOpen(false);
      return;
    }
    setError(null);
    setPreviewState('idle');
    if (hasAccess) {
      loadDocuments();
    }
  }, [isOpen, hasAccess, token, setDocumentPreviewTarget, loadDocuments, resetPreviewContent]);

  useEffect(() => {
    if (isOpen && initialDocName && hasAccess && token && documents.length > 0) {
      // Find the document by name (could be in any folder)
      const doc = documents.find(d => d.name === initialDocName || d.path === initialDocName);
      if (doc) {
        handlePreview(doc);
      }
    }
  }, [isOpen, initialDocName, hasAccess, token, documents, handlePreview]);

  useEffect(() => () => {
    resetPreviewContent();
  }, [resetPreviewContent]);

    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Document Preview" maxWidthClass="max-w-6xl">
        <style>
          {`
            .doc-preview .page-separator {
              width: 100%;
              border-top: 2px dashed ${theme === 'dark' ? '#4b5563' : '#cbd5e1'};
              margin: 1.5rem 0;
              opacity: 0.8;
            }
            .doc-preview .page-separator::after {
              content: 'Page break';
              display: block;
              margin-top: 0.25rem;
              font-size: 0.75rem;
              color: ${theme === 'dark' ? '#9ca3af' : '#6b7280'};
              text-align: center;
            }
          `}
        </style>
        {!hasAccess ? (
          <div className="flex flex-col gap-3 text-sm">
            <div className="flex items-center gap-2 text-amber-600">
              <AlertCircle size={18} />
              <span>Only admin users can preview documents.</span>
            </div>
            <p className="text-gray-500">Contact an administrator if you need access.</p>
          </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <FileText size={18} className={theme === 'dark' ? 'text-blue-300' : 'text-blue-600'} />
              <span className="font-semibold">
                {documents.length} document{documents.length !== 1 ? 's' : ''} available
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={openClearModal}
                disabled={!isAdmin || documents.length === 0 || listState === 'loading' || clearState === 'loading'}
                title={!isAdmin ? "Only admin users can clear the knowledge base" : ""}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  theme === 'dark'
                    ? 'bg-red-950/60 text-red-200 hover:bg-red-900/70 disabled:bg-gray-900 disabled:text-gray-500'
                    : 'bg-red-50 text-red-700 hover:bg-red-100 disabled:bg-gray-100 disabled:text-gray-400'
                }`}
              >
                <Trash2 size={16} />
                Clear KB
              </button>
              <button
                type="button"
                onClick={loadDocuments}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-900 text-gray-200 hover:bg-gray-800'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                <RefreshCcw size={16} />
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
          {notice && !error && (
            <div className="flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-50 p-3 text-sm text-emerald-700">
              <CheckCircle size={16} />
              <span>{notice}</span>
            </div>
          )}

          {/* Storage Usage Bar */}
          {token && <StorageUsageBar token={token} theme={theme} />}

          <div className="flex flex-col gap-4 md:flex-row">
            <div
              className={`md:w-1/4 rounded-xl border text-xs ${
                theme === 'dark' ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className={`border-b px-3 py-2 font-semibold ${theme === 'dark' ? 'border-gray-800' : 'border-gray-200'}`}>
                Files ({filteredDocuments.length})
              </div>
              
              {/* Search Bar */}
              <div className={`border-b px-3 py-2 ${theme === 'dark' ? 'border-gray-800' : 'border-gray-200'}`}>
                <div className={`flex items-center gap-2 rounded-lg px-3 py-2 ${
                  theme === 'dark' ? 'bg-gray-800 text-gray-200' : 'bg-white border border-gray-300'
                }`}>
                  <Search size={16} className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} />
                  <input
                    type="text"
                    placeholder="Search documents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`flex-1 bg-transparent outline-none text-xs placeholder-gray-500 ${
                      theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                    }`}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className={`p-1 rounded hover:bg-opacity-50 ${
                        theme === 'dark' ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-200'
                      }`}
                      title="Clear search"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>

              <div className="max-h-[70vh] overflow-y-auto p-2 space-y-1">
                {listState === 'loading' && (
                  <div className="flex items-center justify-center py-6 text-xs text-gray-500">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading documents...
                  </div>
                )}
                {listState === 'ready' && documents.length === 0 && (
                  <div className="py-4 text-center text-xs text-gray-500">No documents available.</div>
                )}
                {listState === 'ready' && documents.length > 0 && filteredDocuments.length === 0 && (
                  <div className="py-4 text-center text-xs text-gray-500">No documents match your search.</div>
                )}
                {listState === 'ready' && filteredDocuments.length > 0 && (
                  <div className="space-y-2">
                    {Object.entries(groupDocumentsByFolder(filteredDocuments)).map(([folder, docs]) => (
                      <div key={folder}>
                        {/* Folder header */}
                        <button
                          type="button"
                          title={folder}
                          onClick={() => {
                            const newExpanded = new Set(expandedFolders);
                            if (newExpanded.has(folder)) {
                              newExpanded.delete(folder);
                            } else {
                              newExpanded.add(folder);
                            }
                            setExpandedFolders(newExpanded);
                          }}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left font-semibold transition-colors group relative ${
                            theme === 'dark'
                              ? 'text-gray-300 hover:bg-gray-800'
                              : 'text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <span className="text-xs">{expandedFolders.has(folder) ? '▼' : '▶'}</span>
                          <span className="text-xs">{getFolderIcon()}</span>
                          <span className="flex-1 truncate text-xs">{folder}</span>
                          <span className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                            ({docs.length})
                          </span>
                        </button>
                        
                        {/* Files in folder */}
                        {expandedFolders.has(folder) && (
                          <ul className="ml-4 mt-1 space-y-1">
                            {docs.map((doc) => (
                              <li key={doc.path}>
                                <div className="flex items-start gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handlePreview(doc)}
                                    className={`flex-1 min-w-0 rounded-lg px-3 py-1.5 text-left break-words leading-snug transition-colors ${
                                      selectedDocPath === doc.path
                                        ? theme === 'dark'
                                          ? 'bg-blue-600 text-white'
                                          : 'bg-blue-100 text-blue-900'
                                        : theme === 'dark'
                                        ? 'text-gray-200 hover:bg-gray-800'
                                        : 'text-gray-800 hover:bg-gray-200'
                                    }`}
                                    title={doc.path}
                                  >
                                    {doc.name}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => openDeleteModal(doc)}
                                    disabled={!isAdmin || deleteState === 'loading' || clearState === 'loading'}
                                    className={`shrink-0 mt-0.5 rounded-lg p-2 transition-colors ${
                                      theme === 'dark'
                                        ? 'text-gray-400 hover:bg-gray-800 hover:text-red-300 disabled:text-gray-600 disabled:hover:bg-transparent'
                                        : 'text-gray-500 hover:bg-gray-200 hover:text-red-600 disabled:text-gray-300 disabled:hover:bg-transparent'
                                    }`}
                                    aria-label={`Delete ${doc.name}`}
                                    title={!isAdmin ? "Only admin users can delete documents" : `Delete ${doc.path}`}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div
              className={`md:w-3/4 rounded-xl border ${
                theme === 'dark' ? 'border-gray-800 bg-gray-950' : 'border-gray-200 bg-white'
              }`}
            >
              <div className={`border-b px-4 py-3 text-sm font-semibold ${theme === 'dark' ? 'border-gray-800' : 'border-gray-200'}`}>
                {selectedDocPath ? `Preview: ${selectedDocPath}` : 'Preview'}
              </div>
              <div className="max-h-[75vh] overflow-y-auto p-4 doc-preview space-y-4">
                {previewState === 'idle' && <p className="text-sm text-gray-500">Select a document to preview.</p>}
                {previewState === 'loading' && (
                  <div className="flex items-center text-sm text-gray-500">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading ...
                  </div>
                )}
                {previewState === 'ready' && previewContent.type === 'html' && (
                  <div
                    className={`prose max-w-none ${theme === 'dark' ? 'prose-invert' : ''}`}
                    dangerouslySetInnerHTML={{ __html: previewContent.data }}
                  />
                )}
                {previewState === 'ready' && previewContent.type === 'pdf' && (
                  <div className="h-[70vh]">
                    <iframe
                      title={`Preview ${selectedDocPath || 'document'}`}
                      src={previewContent.data}
                      className="h-full w-full rounded-lg border border-gray-200 dark:border-gray-800"
                    />
                  </div>
                )}
                {previewState === 'ready' && previewContent.type === 'table' && (
                  <div className="max-h-[70vh] overflow-auto rounded-lg border border-gray-200 dark:border-gray-800">
                    <table className="min-w-full text-xs">
                      <thead className={theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'}>
                        <tr>
                          {previewContent.data.headers.map((header, idx) => (
                            <th
                              key={idx}
                              className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700 dark:border-gray-800 dark:text-gray-200"
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewContent.data.rows.length === 0 && (
                          <tr>
                            <td
                              className="px-3 py-2 text-sm text-gray-500 dark:text-gray-300"
                              colSpan={Math.max(previewContent.data.headers.length, 1)}
                            >
                              No data available in this sheet.
                            </td>
                          </tr>
                        )}
                        {previewContent.data.rows.map((row, rowIndex) => (
                          <tr
                            key={rowIndex}
                            className={theme === 'dark' ? 'odd:bg-gray-950 even:bg-gray-900' : 'odd:bg-white even:bg-gray-50'}
                          >
                            {row.map((cell, cellIdx) => (
                              <td
                                key={cellIdx}
                                className="border-b border-gray-200 px-3 py-2 text-gray-800 dark:border-gray-800 dark:text-gray-100"
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {(previewContent.data.truncatedRows || previewContent.data.truncatedColumns) && (
                      <div className="border-t border-gray-200 px-3 py-2 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
                        {previewContent.data.truncatedRows && `Showing first ${MAX_TABLE_ROWS} rows. `}
                        {previewContent.data.truncatedColumns && `Showing first ${MAX_TABLE_COLUMNS} columns.`}
                      </div>
                    )}
                  </div>
                )}
                {previewState === 'ready' && previewContent.type === 'none' && (
                  <p className="text-sm text-gray-500">Preview not available for this file type.</p>
                )}
                {previewState === 'error' && (
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <AlertCircle size={16} />
                    <span>Unable to load preview.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <Modal
        isOpen={Boolean(deleteTarget)}
        onClose={closeDeleteModal}
        title="Delete document?"
        maxWidthClass="max-w-sm"
      >
        <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
          This will remove "<strong>{deleteTarget?.path || deleteTarget?.name}</strong>" from the filesystem and refresh the knowledge base index.
        </p>
        {deleteTarget?.folder && (
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-4 text-xs`}>
            Location: {deleteTarget.folder}
          </p>
        )}
        {deleteError && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-50 p-2 text-xs text-red-700">
            <AlertCircle size={14} />
            <span>{deleteError}</span>
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={closeDeleteModal}
            disabled={deleteState === 'loading'}
            className={`px-4 py-2 rounded-lg border transition-colors ${
              theme === 'dark'
                ? 'border-gray-700 text-gray-200 hover:bg-gray-800'
                : 'border-gray-300 text-gray-800 hover:bg-gray-100'
            }`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmDelete}
            disabled={deleteState === 'loading'}
            className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-60"
          >
            {deleteState === 'loading' ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </Modal>
      <Modal
        isOpen={clearModalOpen}
        onClose={closeClearModal}
        title="Clear knowledge base?"
        maxWidthClass="max-w-sm"
      >
        <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-4`}>
          This will delete all documents from the filesystem and refresh the knowledge base index.
        </p>
        <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-4 text-xs`}>
          Documents to remove: {documents.length}
        </p>
        {clearError && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-50 p-2 text-xs text-red-700">
            <AlertCircle size={14} />
            <span>{clearError}</span>
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={closeClearModal}
            disabled={clearState === 'loading'}
            className={`px-4 py-2 rounded-lg border transition-colors ${
              theme === 'dark'
                ? 'border-gray-700 text-gray-200 hover:bg-gray-800'
                : 'border-gray-300 text-gray-800 hover:bg-gray-100'
            }`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmClearAll}
            disabled={clearState === 'loading'}
            className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-60"
          >
            {clearState === 'loading' ? 'Clearing...' : 'Clear all'}
          </button>
        </div>
      </Modal>
    </Modal>
  );
};

export default DocumentPreviewer;
